import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import {
  Button,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { RefreshIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useResetAffordaPortalApplication from '../../../hooks/useResetAffordaPortalApplication';
import ResetAffordaPortalApplicationModal from '../../ResetAffordaPortalApplicationModal';
import messages from './messages';
import useHasPermission from '../../../hooks/useHasPermission';
import useIntl from '../../../hooks/useIntl';
import useApplicationFeesQuery from '../../../hooks/data-fetching/useApplicationFeesQuery';
import useAffordaPortalDetailQuery from '../../../hooks/data-fetching/useAffordaPortalDetailQuery';

/**
 * Checks whether the application-fees API response contains any
 * configured fee with a positive amount.
 *
 * The response may or may not be wrapped in a `to` property depending
 * on which endpoint/client is used. Normalise to handle both shapes.
 */
function hasConfiguredApplicationFees(applicationFees) {
  const fees = applicationFees?.to ?? applicationFees;
  return (
    (fees?.resident?.admin?.amount ?? 0) > 0 ||
    (fees?.resident?.application?.amount ?? 0) > 0 ||
    (fees?.resident?.depositAlternative?.amount ?? 0) > 0 ||
    (fees?.security?.deposit?.amount ?? 0) > 0
  );
}

function ResetAffordaPortalButton({
  application,
  selectedProperty,
  onResetSuccess,
  disabled = false,
  currentUserId,
}) {
  const { id: propertyId, organizationId } = selectedProperty || {};
  const {
    applicants,
    id: applicationId,
    areFeesPaid,
    areFeesApplied,
  } = application || {};

  const { portalUserId, prospectId } = useMemo(() => {
    const primaryProspectApplicant = applicants?.find(
      (applicant) => applicant?.applicantCustomer?.isProspect === true,
    );

    const primaryApplicantCustomer =
      primaryProspectApplicant?.applicantCustomer?.customer;

    return {
      portalUserId: primaryApplicantCustomer?.pu?.id ?? null,
      prospectId: primaryApplicantCustomer?.prospectId ?? null,
    };
  }, [applicants]);

  const [showModal, setShowModal] = useState(false);

  const intl = useIntl();

  const adultApplicantsCount = useMemo(
    () =>
      (applicants ?? []).filter((applicant) => {
        const typeName = applicant?.applicantType?.name;
        return typeName !== 'Minor' && typeName !== 'Pet';
      }).length,
    [applicants],
  );

  const {
    data: applicationFeesResponse,
    isLoading: isLoadingApplicationFees,
    isError: isErrorApplicationFees,
  } = useApplicationFeesQuery({
    organizationId,
    propertyId,
    adultApplicantsCount,
  });

  const {
    data: portalDetailResponse,
    isLoading: isLoadingPortalDetail,
    isError: isErrorPortalDetail,
  } = useAffordaPortalDetailQuery({
    organizationId,
    propertyId,
    portalUserId,
  });

  const isQueryError = isErrorApplicationFees || isErrorPortalDetail;

  const isPaymentSectionCompleted =
    portalDetailResponse?.sectionsProgress?.payments?.isComplete === true;

  const hasFeesConfigured = useMemo(() => {
    if (!applicationFeesResponse) return false;
    return hasConfiguredApplicationFees(applicationFeesResponse);
  }, [applicationFeesResponse]);

  // Block reset when:
  // - Payment section is completed in the portal — always blocks, regardless of other values
  // - Fees have been paid (areFeesPaid) — always blocks
  // - Fees are applied (areFeesApplied) AND actual fees exist — blocks
  // If areFeesApplied is true but no real fees exist, allow reset.
  // If areFeesApplied is false but fees exist, allow reset.
  const isBlockedByFeeState =
    isPaymentSectionCompleted ||
    areFeesPaid ||
    (areFeesApplied && hasFeesConfigured);

  const { resetAppPermission } = useFlags();
  const hasResetPermission = useHasPermission('application-reset');
  const shouldCheckPermission = resetAppPermission;
  const effectiveHasPermission = shouldCheckPermission
    ? hasResetPermission
    : true;

  const { mutate: resetApplicationMutate, isLoading } =
    useResetAffordaPortalApplication({
      options: {
        onSuccess: () => {
          toastr.success('Success', 'Application reset successfully');
          onResetSuccess();

          // Redirect after successful reset to Manage Prospects (Scenario 5 requirement)
          setTimeout(() => {
            if (propertyId) {
              window.location.href = `/property/${propertyId}/management`;
            }
          }, 600);
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === 'string' ? error : 'Failed to reset application');

          toastr.error('Error', errorMessage);
        },
      },
    });

  const hasPortalUserId = portalUserId?.length > 0;

  const handleResetClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirmReset = useCallback(async () => {
    resetApplicationMutate({
      organizationId,
      propertyId,
      portalUserId,
      applicationId,
      prospectId,
      currentUserId: currentUserId || 'system', // Fallback to 'system' if not available
    });
  }, [
    resetApplicationMutate,
    organizationId,
    propertyId,
    portalUserId,
    applicationId,
    prospectId,
    currentUserId,
  ]);

  const shouldDisable =
    disabled ||
    isLoading ||
    isLoadingApplicationFees ||
    isLoadingPortalDetail ||
    isQueryError ||
    !hasPortalUserId ||
    isBlockedByFeeState ||
    (shouldCheckPermission && !effectiveHasPermission);

  const getTooltipMessage = () => {
    if (shouldCheckPermission && !effectiveHasPermission) {
      return intl.formatMessage(messages.noPermissionTooltip);
    }
    if (isQueryError) {
      return intl.formatMessage(messages.queryErrorTooltip);
    }
    if (isPaymentSectionCompleted) {
      return intl.formatMessage(messages.paymentSectionCompletedTooltip);
    }
    if (areFeesPaid) {
      return intl.formatMessage(messages.feesPaidTooltip);
    }
    if (areFeesApplied && hasFeesConfigured) {
      return intl.formatMessage(messages.feesAppliedTooltip);
    }
    return '';
  };

  return (
    <>
      <Tooltip title={getTooltipMessage()}>
        <Button
          onClick={handleResetClick}
          disabled={shouldDisable}
          variant="primarySubtle"
          startIcon={<RefreshIcon />}
          size="small"
        >
          <FormattedMessage {...messages.resetButtonText} />
        </Button>
      </Tooltip>

      <ResetAffordaPortalApplicationModal
        open={showModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmReset}
        isLoading={isLoading}
      />
    </>
  );
}

export default ResetAffordaPortalButton;
